import { Installation } from '../models'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { fetchOwnedInstallations } from '../api/installation.api'

import { useUser } from 'src/Hooks/useUser'
import { isUndefined } from 'lodash'
import { AxiosError, isAxiosError } from 'axios'
import { usePortalQuery } from 'src/Libs/api'

interface OwnedInstallationsHook {
  ownedInstallations: Installation[]
  isUserAnOwner: boolean | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
}

/**
 * hook to get all installations that are owned by the current user
 */
const useOwnedInstallations = (): OwnedInstallationsHook => {
  const authFetch = useAuthFetchWithResponse()
  const { userData } = useUser()

  const queryFn = async (): Promise<Installation[] | null> => {
    const { error: fetchError, hasErrors, value } = await fetchOwnedInstallations(authFetch, userData?.ID)

    if (hasErrors && isAxiosError(fetchError)) {
      throw new Error(fetchError?.message, { cause: fetchError as AxiosError })
    } else if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }

    return value
  }

  const {
    data: ownedInstallations,
    isLoading,
    isError,
    error,
  } = usePortalQuery({
    queryKey: ['owned-installations', userData],
    queryFn,
    enabled: !isUndefined(userData?.ID),
    retry: 0,
  })

  let isUserAnOwner: boolean | undefined
  if (!isLoading && ownedInstallations) {
    isUserAnOwner = ownedInstallations.length > 0
  }

  return { ownedInstallations: ownedInstallations ?? [], isUserAnOwner, isLoading, isError, error }
}

export default useOwnedInstallations
